import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCachedTexts } from '../hooks/useCachedTexts';
import { getTimelineData, getOptimizedImageUrl } from '../sanity/client';
import ScrollReveal from './ScrollReveal';
import LiquidEther from './LiquidEther';
import './TimelineSection.css';

// Memoized LiquidEther to prevent re-initialization on parent re-renders
const MemoizedLiquidEther = memo(LiquidEther);

const TimelineSection = () => {
  const { t } = useTranslation();
  const { getText } = useCachedTexts('home_timeline');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStar, setShowStar] = useState(false);
  const [starRotation, setStarRotation] = useState(0);
  const [starScale, setStarScale] = useState(null); // null until measured from pinned star
  const [starOnTimeline, setStarOnTimeline] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [emitterX, setEmitterX] = useState(0); // Calculated based on line position
  const [starLeftProgress, setStarLeftProgress] = useState(0); // 0 = center, 1 = left position
  const [starVerticalProgress, setStarVerticalProgress] = useState(0); // 0 = 50vh, 1 = 75vh
  const [starAtEnd, setStarAtEnd] = useState(false); // Star has reached end of timeline
  const [starEndTop, setStarEndTop] = useState(null); // Fixed position when star reaches end
  const [pinnedStarHidden, setPinnedStarHidden] = useState(false); // Track when pinned star is hidden
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const starRef = useRef(null);
  const handoffScaleRef = useRef(null); // Store the calculated handoff scale
  const handoffScrollYRef = useRef(null); // Store the scroll position when star first appears

  useEffect(() => {
    console.log('🔵 TimelineSection: Component mounted, fetching data...');
    const fetchEvents = async () => {
      try {
        const timelineEvents = await getTimelineData();
        console.log('🔵 TimelineSection: Fetched events:', timelineEvents);
        setEvents(timelineEvents);
      } catch (error) {
        console.error('🔴 TimelineSection: Error fetching timeline:', error);
      } finally {
        setLoading(false);
        console.log('🔵 TimelineSection: Loading complete');
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    let ticking = false;
    
    const updateTrail = () => {
      if (sectionRef.current && timelineRef.current) {
        const sectionRect = sectionRef.current.getBoundingClientRect();
        const timelineRect = timelineRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // Check if pinned star is hidden (MagicTimelineSection's pinned phase has ended)
        // The pinned phase ends when magic-timeline-section is no longer pinned at top
        const magicSection = document.querySelector('.magic-timeline-section');
        let isPinnedPhaseEnded = false;
        if (magicSection) {
          const magicRect = magicSection.getBoundingClientRect();
          // Pinned star is hidden when magic section starts scrolling away (top < 0)
          // Using 0 instead of -10 for seamless handoff - star appears immediately when pinned phase ends
          isPinnedPhaseEnded = magicRect.top < 0;
          setPinnedStarHidden(isPinnedPhaseEnded);
        }
        
        // Show star ONLY when:
        // 1. Timeline section top reaches bottom of viewport (start of unpinned phase)
        // 2. AND MagicTimelineSection's pinned phase has ended
        // This prevents the star from showing at page load before the animation completes
        const shouldShow = sectionRect.top <= viewportHeight && isPinnedPhaseEnded;
        setShowStar(shouldShow);
        
        // Capture the scroll position and measure pinned star size when handoff occurs
        if (shouldShow && handoffScrollYRef.current === null && isPinnedPhaseEnded) {
          handoffScrollYRef.current = window.scrollY;
          console.log('🎯 Handoff point captured at scrollY:', handoffScrollYRef.current);
          
          // Calculate the exact scale needed for seamless handoff
          // The pinned star is in an SVG with viewBox="0 0 979 212"
          // The star path is approximately 87px wide in viewBox coordinates (from path: ~318 to ~405)
          // GSAP applies scale 1.5 to it
          if (handoffScaleRef.current === null) {
            const logoSvg = document.querySelector('.logo-svg');
            if (logoSvg) {
              const svgRect = logoSvg.getBoundingClientRect();
              const viewBoxWidth = 979;
              const starViewBoxWidth = 87; // Approximate width of star path in viewBox
              const gsapScale = 1;
              
              // Calculate rendered star width:
              // (SVG rendered width / viewBox width) * star viewBox width * GSAP scale
              const svgScaleFactor = svgRect.width / viewBoxWidth;
              const renderedStarWidth = svgScaleFactor * starViewBoxWidth * gsapScale;
              
              // The unpinned star SVG has width=120 and viewBox width=139
              // The actual star path inside is about 87px wide (similar to pinned)
              // So the star renders at: (120 / 139) * 87 = ~75px at scale 1
              const unpinnedStarBaseWidth = (120 / 139) * 87;
              
              // Calculate scale needed to match
              let calculatedScale = renderedStarWidth / unpinnedStarBaseWidth;
              
              // Cap the scale to prevent star from being too large
              if (window.innerWidth <= 768) {
                // On mobile, cap to ensure star doesn't touch left edge
                const maxScale = 0.85;
                calculatedScale = Math.min(calculatedScale, maxScale);
              } else {
                // On desktop, cap for better visual proportion
                const maxScale = 1.2;
                calculatedScale = Math.min(calculatedScale, maxScale);
              }
              
              handoffScaleRef.current = calculatedScale;
              setStarScale(calculatedScale);
              console.log('🎯 SVG width:', svgRect.width, 'Rendered star:', renderedStarWidth, 'Scale:', calculatedScale);
            }
          }
        }
        
        // Calculate rotation starting from 180° at handoff point
        // Only rotate if star hasn't reached the end
        if (handoffScrollYRef.current !== null && !starAtEnd) {
          const baseRotation = 180; // Starting rotation from MagicTimelineSection Phase 2
          const scrollSinceHandoff = window.scrollY - handoffScrollYRef.current;
          const scrollRotation = (scrollSinceHandoff / 1000) * 360;
          const rotation = baseRotation + scrollRotation;
          setStarRotation(rotation);
          
          // Calculate vertical progress for star moving from 50vh to 75vh
          const sectionTop = sectionRect.top;
          const startY = viewportHeight; // section.top = 100vh (bottom of viewport)
          const endY = viewportHeight * 0.75; // section.top = 75vh
          const totalYDistance = startY - endY;
          const currentYDistance = sectionTop - endY;
          const vertProgress = Math.max(0, Math.min(1, 1 - (currentYDistance / totalYDistance)));
          setStarVerticalProgress(vertProgress);
          
          // On mobile, calculate curve progress from center to left
          // Progress goes from 0 (center) to 1 (left) as timeline-section.top approaches 75vh
          if (window.innerWidth <= 768) {
            setStarLeftProgress(vertProgress);
          } else {
            setStarLeftProgress(0);
          }
        }
        
        // Update trail drawing effect and star blend mode
        // Check if star has reached the center of the last timeline item
        const starCenterY = viewportHeight * 0.75;
        const timelineItems = timelineRef.current.querySelectorAll('.timeline-item');
        const lastItem = timelineItems[timelineItems.length - 1];
        let hasReachedEnd = false;
        
        if (lastItem) {
          const lastItemRect = lastItem.getBoundingClientRect();
          const lastItemCenterY = lastItemRect.top + lastItemRect.height / 2;
          hasReachedEnd = lastItemCenterY <= starCenterY;
        }
        
        if (hasReachedEnd) {
          // Star has reached or passed the center of last item
          // Calculate position relative to document (not section) since star is outside section
          if (lastItem) {
            const lastItemRect = lastItem.getBoundingClientRect();
            const lastItemCenterY = lastItemRect.top + lastItemRect.height / 2;
            // Position relative to document = viewport position + scroll offset
            const starAbsoluteTop = lastItemCenterY + window.scrollY;
            setStarEndTop(starAbsoluteTop);
          }
          if (!starAtEnd) {
            setStarAtEnd(true);
          }
        } else if (!hasReachedEnd && starAtEnd) {
          // Scrolling back up - star should resume following
          setStarAtEnd(false);
          setStarEndTop(null);
        }
        
        // The lines container is fixed from top:0 to bottom:25vh (i.e., extends to 75vh)
        // We clip from the top based on where the timeline starts
        // clip-top = how much to hide from top (in percentage of the container)
        const starFixedY = viewportHeight * 0.75; // bottom of lines container
        const containerHeight = starFixedY; // container goes from 0 to 75vh
        
        // Calculate where timeline top is relative to viewport top
        const timelineTopInViewport = timelineRect.top;
        
        // Clip percentage: how much of the container to hide from top
        // When timeline top is at viewport top (0), clip = 0% (show all)
        // When timeline top is at 75vh, clip = 100% (hide all)
        let clipTop = Math.max(0, Math.min(100, (timelineTopInViewport / containerHeight) * 100));
        
        // Also cap at last item center
        if (hasReachedEnd && lastItem) {
          const lastItemRect = lastItem.getBoundingClientRect();
          const lastItemCenterY = lastItemRect.top + lastItemRect.height / 2;
          // The line should stop at lastItemCenterY
          // Convert to clip percentage
          const stopClip = Math.max(0, (lastItemCenterY / containerHeight) * 100);
          clipTop = Math.max(clipTop, 100 - (100 - stopClip));
        }
        
        if (timelineRect.top <= starFixedY && !hasReachedEnd) {
          timelineRef.current.classList.add('drawing');
          timelineRef.current.classList.remove('at-end');
          setStarOnTimeline(timelineRect.top <= starFixedY && timelineRect.bottom > starFixedY);
          // Update clip-path to reveal lines from timeline top down to star
          timelineRef.current.style.setProperty('--clip-top', `${clipTop}%`);
        } else if (hasReachedEnd) {
          // Star has stopped - hide the fixed lines, show static version
          timelineRef.current.classList.add('drawing');
          timelineRef.current.classList.add('at-end');
          // Set static line height to reach the last item center
          if (lastItem) {
            const lastItemRect = lastItem.getBoundingClientRect();
            const lastItemCenterY = lastItemRect.top + lastItemRect.height / 2;
            const staticHeight = lastItemCenterY - timelineRect.top;
            timelineRef.current.style.setProperty('--static-line-height', `${staticHeight}px`);
            // For mobile fixed positioning: set bottom position from viewport bottom
            const staticLineBottom = viewportHeight - lastItemCenterY;
            timelineRef.current.style.setProperty('--static-line-bottom', `${staticLineBottom}px`);
          }
          setStarOnTimeline(false);
        } else {
          timelineRef.current.classList.remove('drawing');
          timelineRef.current.classList.remove('at-end');
          setStarOnTimeline(false);
          timelineRef.current.style.setProperty('--clip-top', '100%');
        }
        
        // Fade in/out timeline items when 75vh is about to reach the item's top
        timelineItems.forEach((item) => {
          const itemRect = item.getBoundingClientRect();
          // Trigger when item top is at 75vh 
          if (itemRect.top <= viewportHeight * 0.75) {
            item.classList.add('visible');
          } else {
            item.classList.remove('visible');
          }
        });
      }
      ticking = false;
    };
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateTrail);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateTrail(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, starAtEnd]);

  // Mobile detection, emitterX calculation, and reset handoff on resize
  // Note: starScale is measured dynamically from the pinned star at handoff
  useEffect(() => {
    const calculateEmitterX = (width) => {
      // Match CSS: .timeline-lines-container { left: max(14vw, 100px); }
      // Convert to emitterX coordinate system: -1 (left) to 1 (right), 0 = center
      if (width <= 768) {
        // Mobile: position at the line location
        // Line is at max(14vw, 100px)
        const lineLeft = Math.max(width * 0.14, 100);
        // Convert pixel position to -1 to 1 range
        // lineLeft / width gives 0-1 range from left, multiply by 2 and subtract 1 for -1 to 1
        const normalizedX = (lineLeft / width) * 2 - 1;
        return normalizedX;
      }
      return 0; // Desktop: center
    };

    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setEmitterX(calculateEmitterX(width));
      // Reset all handoff values so they recalculate on next scroll
      handoffScaleRef.current = null;
      handoffScrollYRef.current = null;
      setStarScale(null);
      setStarRotation(180); // Reset to base rotation
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset all animation state when component mounts (e.g., navigating to home)
  useEffect(() => {
    // Reset all handoff values on mount
    handoffScaleRef.current = null;
    handoffScrollYRef.current = null;
    setStarScale(null);
    setStarRotation(180);
    setShowStar(false);
    setStarOnTimeline(false);
    setStarAtEnd(false);
    setStarEndTop(null);
    setStarLeftProgress(0);
  }, []);

  console.log('🔵 TimelineSection: Rendering with loading=', loading, 'events=', events);

  if (loading) return (
    <section className="timeline-section">
      <div className="timeline-container">
        <div className="timeline-loading">Loading timeline...</div>
      </div>
    </section>
  );
  
  if (!events || events.length === 0) {
    return (
      <section className="timeline-section">
        <div className="timeline-container">
          <h2>{getText('title', t('timeline.title'))}</h2>
          <p>No timeline events available.</p>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Fixed star that appears after scrolling past title - outside section to avoid clip-path */}
      <div 
        ref={starRef}
        className={`timeline-star ${showStar ? 'visible' : ''} ${pinnedStarHidden ? 'pinned-hidden' : ''} ${starOnTimeline ? 'on-timeline' : ''} ${starAtEnd ? 'at-end' : ''}`}
        style={{ 
          '--star-scale': showStar ? starScale : 0.01,
          '--star-rotation': `${starRotation}deg`,
          // When star reaches end, switch to absolute positioning within section
          ...(starAtEnd && starEndTop !== null ? {
            position: 'absolute',
            top: `${starEndTop}px`,
            left: isMobile ? 'max(14vw, 100px)' : '50%',
          } : isMobile ? {
            // On mobile, create a loop-de-loop curve from center to left position
            // Horizontal: interpolate from 50% to max(14vw, 100px) in a curve
            left: starLeftProgress >= 1 
              ? 'max(14vw, 100px)' 
              : `calc(50% * ${1 - starLeftProgress} + max(14vw, 100px) * ${starLeftProgress})`,
            // Vertical: start at 50%, arc up, then end at 75% using sine curve
            top: `calc(50% + ${starVerticalProgress * 25}% - ${Math.sin(starLeftProgress * Math.PI) * 15}vh)`,
          } : {
            left: '50%',
            // Desktop: interpolate from 50% to 75% based on vertical progress
            top: `calc(50% + ${starVerticalProgress * 25}%)`,
          }),
          // Always use -50% translateX to center the star on its left position
          // On mobile, interpolate scale down as star moves to the left
          transform: (() => {
            let scale = showStar && starScale !== null ? starScale : 0.01;
            if (isMobile && starScale !== null) {
              const maxScale = 0.85; // Max scale when at left position
              // Interpolate from current scale to maxScale as starLeftProgress goes from 0 to 1
              if (starScale > maxScale) {
                scale = starScale - (starLeftProgress * (starScale - maxScale));
              }
            }
            return `translate(-50%, -50%) scale(${scale}) rotate(${starRotation}deg)`;
          })() 
        }}
      >
        <svg width="90" height="90" viewBox="0 0 90 90" fill="none" style={{ overflow: 'visible' }}>
          <path 
            d="M27.6062 75.892L42.5117 47.096L24.031 20.439L56.096 25.7368L75.6752 -0.0075748L80.4939 31.9752L111.166 42.719L82.1583 57.2334L81.4122 89.6499L58.6588 66.542L27.6062 75.892Z" 
            fill="#ffffff"
            transform="translate(-22.5, 0)"
          />
        </svg>
      </div>
      
      <section className="timeline-section" ref={sectionRef}>
        {/* Floating particles background */}
        <div className="timeline-particles">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="timeline-particle" />
          ))}
        </div>
        
        {/* Liquid Ether background effect - scroll controlled from center */}
        <div className="timeline-liquid-ether">
          <MemoizedLiquidEther
            scrollActive={starOnTimeline && !starAtEnd}
            emitterX={emitterX}
            emitterY={-0.5}
          />
        </div>
        
        <div className="timeline-container">
          <h2 className="timeline-title">{getText('title', t('timeline.title'))}</h2>
        <div className="timeline" ref={timelineRef}>
          {/* Fixed lines that follow the star */}
          <div className="timeline-lines-container">
            <div className="timeline-line timeline-line-left"></div>
            <div className="timeline-line timeline-line-center"></div>
            <div className="timeline-line timeline-line-right"></div>
          </div>
          {/* Static lines that show when star has stopped */}
          <div className="timeline-lines-static">
            <div className="timeline-line timeline-line-left"></div>
            <div className="timeline-line timeline-line-center"></div>
            <div className="timeline-line timeline-line-right"></div>
          </div>
          {events.map((event, index) => {
            // Build array of images: mainImage + optional secondImage
            const displayImages = [];
            if (event.mainImage?.asset) {
              displayImages.push(event.mainImage);
            }
            if (event.secondImage?.asset) {
              displayImages.push(event.secondImage);
            }
            
            const content = (
              <div className="timeline-content">
                {displayImages.length > 0 && (
                  <div className={`timeline-images timeline-images-${displayImages.length}`}>
                    {displayImages.map((img, imgIndex) => (
                      <div key={imgIndex} className="timeline-image">
                        <img 
                          src={getOptimizedImageUrl(img, { width: 400, quality: 85 })} 
                          alt={`${event.title || 'Portfolio item'} - Image ${imgIndex + 1}`}
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                )}
                <div className="timeline-text">
                  {event.title && event.showTitleInTimeline !== false && <h3>{event.title}</h3>}
                </div>
              </div>
            );
            
            // Only wrap in Link if project has content
            return event.hasContent ? (
              <Link 
                key={event._id} 
                to={`/portfolio/${event.slug}`}
                className="timeline-item timeline-item-clickable"
              >
                {content}
              </Link>
            ) : (
              <div 
                key={event._id} 
                className="timeline-item timeline-item-static"
              >
                {content}
              </div>
            );
          })}
        </div>
      </div>
      </section>
    </>
  );
};

export default TimelineSection;
